<script setup lang="ts">
import type { AccordionItem, FormSubmitEvent } from '#ui/types'
import { merge } from 'es-toolkit/compat'

const props = defineProps<{
  action: 'create' | 'edit'
  data: Partial<Salesorderitem>
  onSubmit: (event: FormSubmitEvent<Partial<Salesorderitem>>) => void
  openNestedCallback: (data: Partial<Salesorderitem>) => void
  title?: string
  disabledFields?: (keyof Salesorderitem | string)[]
  readonly?: boolean
}>()

const { user } = useUserSession()
const { userModule } = useModules()
const slideover = useSlideover()

const inert = computed(() => props.readonly || props.data?.immutable || !['admin', 'edit'].includes(userModule.value?.role))
const simple = ref(true)

const state = ref<Partial<Salesorderitem>>(getDefaultSalesorderitem(props.data))

const $salesorders = await useFetch<Salesorder[]>('/api/db/salesorders', {
  query: { id: props.data.salesorderId! },
  default: () => [],
})
const $pricelists = await useFetch<PricelistWithRelations[]>('/api/db/pricelists', {
  query: {
    status: ['completed'],
    $with: relationsPricelists,
    $sort: { column: 'date', direction: 'desc' },
  },
  default: () => [],
})
const $productitems = await useFetch<Productitem[]>('/api/db/productitems', {
  query: { $with: { product: true } },
  default: () => [],
})
const $quoteitems = await useFetch<Quoteitem[]>('/api/db/quoteitems', {
  query: { quoteId: $salesorders.data.value?.[0]?.quoteId || 0 },
  default: () => [],
})
const $serviceitems = await useFetch<Serviceitem[]>('/api/db/serviceitems', {
  query: { $with: { service: true } },
  default: () => [],
})

const utils = useSalesorderitemsUtils({ $pricelists, $productitems, $quoteitems, $serviceitems })

const items = computed<AccordionItem[]>(() => {
  if (['create'].includes(props.action) && simple.value) {
    return [{ slot: 'simple', label: 'Creación rápida', icon: 'i-mdi-lightning-bolt', defaultOpen: true }]
  }
  return [
    {
      slot: 'general',
      label: 'General',
      icon: 'i-mdi-information',
      defaultOpen: true,
    },
    {
      slot: 'details',
      label: 'Detalles',
      icon: ICONS.salesorderitems,
      defaultOpen: true,
    },
    {
      slot: 'taxes',
      label: 'Impuestos',
      icon: 'i-mdi-scale-balance',
      defaultOpen: true,
    },
    {
      slot: 'data',
      label: 'Datos adicionales',
      icon: 'i-mdi-code-json',
      defaultOpen: false,
    },
  ].filter(item => !['data'].includes(item.slot) || state.value.data)
})

const parentLabel = computed(() => state.value.type === 'products' ? 'Producto' : 'Servicio')
const itemTable = computed(() => state.value.type === 'products' ? 'productitems' : 'serviceitems')
const itemIdField = computed(() => state.value.type === 'products' ? 'productitemId' : 'serviceitemId')
const itemToConcept = computed(() => state.value.type === 'products' ? productitemToConcept : serviceitemToConcept)

const selectedTab = ref(
  (!props.data.quoteitemId && (props.data.serviceitemId || props.data.productitemId)) ? 0 : 1,
)
const tabs = [
  {
    label: 'Nuevo',
    icon: 'i-mdi-plus-circle',
  },
  {
    label: 'Cotización',
    icon: ICONS.quotes,
  },
]

const onUpdateType = (value: string) => {
  state.value = getDefaultSalesorderitem(props.data)
  state.value.type = value
  if (value === 'products') {
    state.value.subtype = 'finished-goods'
  }
  else {
    state.value.subtype = 'core'
  }
}

const optionsItems = computed(() => {
  const type = state.value.type
  const subtype = state.value.subtype
  if (!type || !subtype) return []
  if (selectedTab.value === 0) {
    return (
      utils.maps[
        type === 'products' ? 'productitemsByProductType' : 'serviceitemsByServiceType'
      ]?.value
        .get(subtype)
        ?.map(toOption) ?? []
    )
  }
  else {
    const itemsFilteredByType = utils.maps.quoteitemsByType?.value.get(type)
    const itemsFilteredBySubtype = itemsFilteredByType?.filter(item => item.subtype === subtype)
    return itemsFilteredBySubtype.map(toOption) ?? []
  }
})

const onUpdateItem = (value: string) => {
  if (!value) return

  if (selectedTab.value === 1) {
    state.value.quoteitemId = value
    const item = utils.maps.quoteitemId?.value.get(value) as Quoteitem
    state.value.name = item.name
    state.value.description = item.description
    state.value.image = item.image
    state.value.sku = item.sku
    state.value.quantity = item.quantity
    state.value.unitPrice = item.unitPrice
    state.value.discount = item.discount
    state.value.fiscalData = item.fiscalData
    state.value.taxes = item.taxes
    state.value.amount = item.amount
    state.value.total = item.total
    state.value.productitemId = item.productitemId
    state.value.quoteitemId = item.id
    state.value.serviceitemId = item.serviceitemId
    return
  }
  else {
    const item = utils.maps[itemIdField.value]?.value.get(value) as (Productitem | Serviceitem) & { product?: Product, service?: Service }
    const pricelists = utils.maps.pricelistsByType?.value.get(state.value?.type) ?? [] as PricelistWithRelations[]
    const basePrice = getPriceForItem(pricelists, state.value?.type, item.id)
    state.value = merge(state.value, itemToConcept.value(item, basePrice))
  }
}

watch(() => state.value.fiscalData?.satTaxObject, (satTaxObject) => {
  const disable = ({ enabled, ...rest }) => ({ enabled: false, ...rest })
  // disable all taxes if satTaxObject is "01 - Sin objeto de impuestos"
  if (['01'].includes(satTaxObject)) {
    state.value.taxes.transfers = state.value.taxes.transfers.map(disable)
    state.value.taxes.retentions = state.value.taxes.retentions.map(disable)
  }
})

const onUpdateSalesorderitemTaxes = () => {
  const calculateTax = (taxItem: ItemTaxes) => ({
    ...taxItem,
    base: Number(state.value.amount),
    amount: taxItem.enabled ? Number(state.value.amount) * Number(taxItem.satRateOrQuota) : 0,
  })

  const { transfers, retentions } = state.value.taxes!

  state.value.taxes.transfers = transfers.map(calculateTax)
  state.value.taxes.retentions = retentions.map(calculateTax)
}

watchDebounced([
  () => state.value.quantity,
  () => state.value.unitPrice,
  () => state.value.discount.value,
  () => state.value.discount.type,
  () => state.value.discount.amount,
  () => state.value.taxes.transfers,
  () => state.value.taxes.retentions,
], () => {
  const { quantity = 0, unitPrice = 0, discount } = state.value
  const amount = quantity * unitPrice || 0
  const discountAmount = discount!.type! === 'percent'
    ? (amount * (discount.value ?? 0))
    : (discount.value ?? 0)
  state.value.discount = { ...discount, amount: discountAmount }
  state.value.amount = amount - discountAmount
  onUpdateSalesorderitemTaxes()
}, { debounce: 50 })

const loading = ref(false)

const onSubmitSalesorderitem = async (event: FormSubmitEvent<Partial<Salesorderitem>>) => {
  event.data = merge(state.value, event.data) // WORKAROUND: UForm drops nested props on validation
  loading.value = true
  if (event.data.type === 'products') delete event.data.serviceitemId
  else delete event.data.productitemId
  event.data.total = event.data.amount + event.data.taxes.transfers
    .reduce((acc, tax) => acc + tax.amount, 0) - event.data.taxes.retentions
    .reduce((acc, tax) => acc + tax.amount, 0)
  await props.onSubmit(event)
}

const salesorderitems$ = useSalesorderitems()

const schema = computed(() => ['products'].includes(state.value.type)
  ? salesorderitems$.schemaProducts
  : salesorderitems$.schemaServices,
)
</script>

<template>
  <UDashboardSlideover prevent-close :ui="{ width: 'min-w-[40vw]' }">
    <template #title>
      <SlideoverTitle :title="title" :inert="inert" />
    </template>
    <UForm
      id="form.salesorderitems"
      :validate-on="['submit']"
      :schema="schema"
      :state="state"
      :inert="inert"
      @error="onFormError"
      @submit="onSubmitSalesorderitem"
    >
      <UAccordion
        multiple
        :items="items"
        :ui="{ item: { base: 'py-1 px-2 space-y-2 text-sm' } }"
      >
        <template #simple>
          <UTabs
            v-model="selectedTab"
            :items="tabs"
            @update:model-value="state = { ...getDefaultSalesorderitem(props.data), type: state.type }"
          />
          <UFormGroup
            label="Tipo"
            name="type"
            :required="isRequired(schema, 'type')"
          >
            <SelectMenuBaseInfo
              v-model="state.type"
              :options="utils.options.type"
              autofocus
              :disabled="disabledFields?.includes('type')"
              @update:model-value="onUpdateType"
            />
          </UFormGroup>

          <UFormGroup
            :label="`Tipo de ${parentLabel}`"
            name="subtype"
            :required="isRequired(schema, 'subtype')"
          >
            <SelectMenuBaseInfo
              v-if="state.type === 'products'"
              v-model="state.subtype"
              :options="optionsProducts.type"
              disabled
            />
            <SelectMenuBaseInfo
              v-else
              v-model="state.subtype"
              :options="optionsServices.type"
              :disabled="disabledFields?.includes('subtype')"
            />
          </UFormGroup>

          <UFormGroup
            :label="`Item`"
            :name="itemIdField"
            :required="isRequired(schema, itemIdField)"
          >
            <SelectMenuBaseReference
              v-if="selectedTab === 0"
              v-model="state"
              v-bind="{
                tableName: itemTable,
                idField: itemIdField,
                utils: utils,
                options: optionsItems,
                map: utils.maps[itemIdField],
                disabled: [itemIdField].includes(disabledFields),
                onClickUpdate: onUpdateItem,
              }"
            />
            <SelectMenuBaseReference
              v-else
              v-model="state"
              v-bind="{
                tableName: 'quoteitems',
                idField: 'quoteitemId',
                utils: utils,
                options: optionsItems,
                map: utils.maps.quoteitemId,
                disabled: [itemIdField, 'quoteitemId'].includes(disabledFields),
                onClickUpdate: onUpdateItem,
              }"
            />
          </UFormGroup>

          <div class="grid md:grid-cols-2 gap-2">
            <UFormGroup
              label="Cantidad"
              name="quantity"
              :required="isRequired(schema, 'quantity')"
            >
              <UInput
                v-model="state.quantity"
                type="number"
                inputmode="decimal"
                :min="0.00"
                :step="0.01"
                :disabled="disabledFields?.includes('quantity')"
              />
            </UFormGroup>

            <UFormGroup
              label="Unidad"
              name="fiscalData.satUnitKey"
              :required="isRequired(schema, 'fiscalData.satUnitKey')"
            >
              <SelectMenuBase
                v-model="state.fiscalData.satUnitKey"
                :disabled="disabledFields?.includes('fiscalData.satUnitKey')"
                :options="optionsSAT.productUnitId"
              />
            </UFormGroup>

            <UFormGroup
              :label="`Precio U (${defaultAppVariables.currency})`"
              name="unitPrice"
              :required="isRequired(schema, 'unitPrice')"
            >
              <InputCurrency
                v-model="state.unitPrice"
                :currency="defaultAppVariables.currency"
                icon
                :disabled="disabledFields?.includes('unitPrice')"
              />
            </UFormGroup>

            <UFormGroup
              :label="`Subtotal (${defaultAppVariables.currency})`"
              name="amount"
              :required="isRequired(schema, 'amount')"
            >
              <InputReadOnly
                :model-value="toCurrency(isNaN(state.amount) ? 0 : (state.amount ?? 0), defaultAppVariables.currency)"
              />
            </UFormGroup>
          </div>

          <SlideoverButtonExpand @click="simple = false" />
        </template>

        <template #general>
          <UTabs
            v-model="selectedTab"
            :items="tabs"
            @update:model-value="state = { ...getDefaultSalesorderitem(props.data), type: state.type }"
          />
          <UFormGroup
            label="Tipo"
            name="type"
            :required="isRequired(schema, 'type')"
          >
            <SelectMenuBaseInfo
              v-model="state.type"
              :options="utils.options.type"
              autofocus
              :disabled="disabledFields?.includes('type')"
              @update:model-value="onUpdateType"
            />
          </UFormGroup>

          <UFormGroup
            :label="`Tipo de ${parentLabel}`"
            name="subtype"
            :required="isRequired(schema, 'subtype')"
          >
            <SelectMenuBaseInfo
              v-if="state.type === 'products'"
              v-model="state.subtype"
              :options="optionsProducts.type"
              disabled
            />
            <SelectMenuBaseInfo
              v-else
              v-model="state.subtype"
              :options="optionsServices.type"
              :disabled="disabledFields?.includes('subtype')"
            />
          </UFormGroup>

          <UFormGroup
            :label="`Item`"
            :name="itemIdField"
            :required="isRequired(schema, itemIdField)"
          >
            <SelectMenuBaseReference
              v-if="selectedTab === 0"
              v-model="state"
              v-bind="{
                tableName: itemTable,
                idField: itemIdField,
                utils: utils,
                options: optionsItems,
                map: utils.maps[itemIdField],
                disabled: [itemIdField].includes(disabledFields),
                onClickUpdate: onUpdateItem,
              }"
            />
            <SelectMenuBaseReference
              v-else
              v-model="state"
              v-bind="{
                tableName: 'quoteitems',
                idField: 'quoteitemId',
                utils: utils,
                options: optionsItems,
                map: utils.maps.quoteitemId,
                disabled: [itemIdField, 'quoteitemId'].includes(disabledFields),
                onClickUpdate: onUpdateItem,
              }"
            />
          </UFormGroup>
        </template>

        <template #details>
          <FieldsetConcept
            v-model="state"
            table-name="salesorderitems"
            :disabled="disabledFields?.includes('concepts')"
            v-bind="{ utils, schema: schema, disabledFields }"
          />
        </template>

        <template #taxes>
          <FieldsetTaxesMX
            v-model="state"
            :disabled="disabledFields?.includes('taxes')"
            :disabled-tabs="['retentions']"
            v-bind="{ utils, schema: schema, disabledFields }"
          />
        </template>

        <template #data>
          <FieldsetData v-model="state.data" :utils="utils" />
        </template>
      </UAccordion>
    </UForm>

    <template v-if="!inert" #footer>
      <div class="grid grid-cols-2 gap-2 w-full">
        <UButton
          color="gray"
          label="Cancelar"
          block
          @click="slideover.close()"
        />
        <UButton
          form="form.salesorderitems"
          type="submit"
          label="Confirmar"
          color="primary"
          block
          :loading="loading"
        />
      </div>
    </template>
  </UDashboardSlideover>
</template>
